import * as RadixTabs from '@radix-ui/react-tabs';
import { useState } from 'react';
import { Link } from 'react-router';
import type { IconType } from '~/components/icons';
import { classNames } from '~/utils';
import { Icon } from '../icon/Icon';
import { Tooltip } from '../tooltip';

export type PageTab = {
  disabled?: boolean;
  icon: IconType;
  isDynamicRoute?: boolean;
  label: string;
  route: string;
  tooltip?: string;
};

export type PageTabsProps = {
  className?: string;
  tabs: PageTab[];
  activeRoute?: string;
};

const iconSize = { height: 16, width: 16 };

export const PageTabs = ({ className, tabs, activeRoute }: PageTabsProps) => {
  return (
    <RadixTabs.Root asChild value={activeRoute}>
      <RadixTabs.List className={classNames('inline-flex bg-neutral-200 p-1', className)}>
        {tabs.map((tab) => (
          <PageTabTrigger activeRoute={activeRoute} key={tab.route} tab={tab} />
        ))}
      </RadixTabs.List>
    </RadixTabs.Root>
  );
};

const PageTabTrigger = ({ tab, activeRoute }: { tab: PageTab; activeRoute?: string }) => {
  const { disabled, route, label, icon, isDynamicRoute, tooltip } = tab;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  function handleTooltipOpenChange(open: boolean) {
    if (!tooltip) {
      return;
    }

    setIsTooltipOpen(open);
  }

  const triggerClassName = classNames(
    'label-200 flex items-center truncate px-2 py-1 text-neutral-600 transition-all',
    {
      'bg-white text-neutral-1000 shadow-xs':
        !disabled && ((isDynamicRoute && activeRoute?.startsWith(route)) || route === activeRoute),
      'cursor-not-allowed opacity-50': disabled,
      'hover:text-neutral-1000': !disabled,
    },
  );

  const content = (
    <>
      <Icon {...iconSize} className="mr-2" icon={icon} />
      {label}
    </>
  );

  const trigger = disabled ? (
    <div className="inline-flex">
      <RadixTabs.Trigger className={triggerClassName} disabled value={route}>
        {content}
      </RadixTabs.Trigger>
    </div>
  ) : (
    <RadixTabs.Trigger asChild className={triggerClassName} value={route}>
      <Link to={route}>{content}</Link>
    </RadixTabs.Trigger>
  );

  return (
    <Tooltip disabled={!tooltip} onOpenChange={handleTooltipOpenChange} open={isTooltipOpen} trigger={trigger}>
      {tooltip}
    </Tooltip>
  );
};
